import { normalizeLine } from '@losan/core';
import { SS27_LINES, type SS27Line } from './objetivosApi.js';

/**
 * Comparable-season baseline used to pre-fill the SS27 form
 * ("autocompletar con SS26/SS25 + X%").
 *
 * Reads the raw seasonal snapshot (same file the dashboard uses) and aggregates,
 * for one agent in the chosen comparable season, the revenue and distinct-client
 * count per SS27 form line. Line normalization is delegated to `normalizeLine`
 * (the single source of truth), so the LOSAN mappings apply here too:
 *   - JUNIOR / TEEN BOY / TEEN GIRL → TEEN
 *   - blank / "(Blank)" collection   → KIDS
 * Lines without sales in that season come back as zero so the UI can disable the
 * autocomplete for them.
 *
 * The client picks the comparable season (SS26 or SS25) over which the desired
 * growth % is applied — SS26 is the default, but SS25 is offered because some
 * lines (e.g. KIDS) have no SS26 sales in this dataset.
 */

/** Seasons the SS27 form can use as a comparable baseline. */
export type ComparableSeason = 'SS26' | 'SS25';
export const COMPARABLE_SEASONS: ComparableSeason[] = ['SS26', 'SS25'];

const FORM_LINES = new Set<string>(SS27_LINES);

interface RawSeasonalRow {
  Agente_Id: string;
  Cliente_Id: string;
  Coleccion: string;
  Temporada: string;
  EUR: number;
}

export interface SeasonBaselineLine {
  facturacion: number;
  clientes: number;
}
export type SeasonBaseline = Record<SS27Line, SeasonBaselineLine>;

const SNAPSHOT_BASE =
  (typeof window !== 'undefined' && (window as { __SNAPSHOT_BASE__?: string }).__SNAPSHOT_BASE__) ||
  '/snapshots';

let cache: Promise<RawSeasonalRow[]> | null = null;
function loadSeasonal(): Promise<RawSeasonalRow[]> {
  if (!cache) {
    cache = fetch(`${SNAPSHOT_BASE}/orders-by-season.json`).then((r) => {
      if (!r.ok) throw new Error(`No se pudo cargar el histórico SS26 (${r.status})`);
      return r.json() as Promise<RawSeasonalRow[]>;
    });
  }
  return cache;
}

function emptyBaseline(): SeasonBaseline {
  return SS27_LINES.reduce((acc, l) => {
    acc[l] = { facturacion: 0, clientes: 0 };
    return acc;
  }, {} as SeasonBaseline);
}

/** Build the comparable-season baseline for one agent. */
export async function fetchSeasonBaseline(
  agentId: string,
  season: ComparableSeason,
): Promise<SeasonBaseline> {
  const rows = await loadSeasonal();
  const out = emptyBaseline();
  // distinct clients per form line
  const clientSets: Record<SS27Line, Set<string>> = SS27_LINES.reduce((acc, l) => {
    acc[l] = new Set<string>();
    return acc;
  }, {} as Record<SS27Line, Set<string>>);

  for (const r of rows) {
    if (String(r.Agente_Id) !== String(agentId)) continue;
    if (r.Temporada !== season) continue;
    const line = normalizeLine(r.Coleccion);
    if (!FORM_LINES.has(line)) continue; // skips DORIA and any non-form line
    const formLine = line as SS27Line;
    out[formLine].facturacion += Number(r.EUR) || 0;
    clientSets[formLine].add(String(r.Cliente_Id));
  }
  for (const l of SS27_LINES) {
    out[l].facturacion = Math.round(out[l].facturacion);
    out[l].clientes = clientSets[l].size;
  }
  return out;
}

/** True if the baseline has any data at all (controls whether to show the
 * autocomplete control). */
export function hasBaselineData(b: SeasonBaseline): boolean {
  return SS27_LINES.some((l) => b[l].facturacion > 0 || b[l].clientes > 0);
}
